import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck, AlertTriangle, FileSpreadsheet, Wrench, GraduationCap,
  CheckCircle2, TrendingUp, TrendingDown, ArrowRight, Plus, MessageSquare, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { ops, fmt, useStores, StoreSwitcher } from '../lib/ops';
import { useAuth } from '../context/AuthContext';

const SEV = {
  urgent: { rank: 4, dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Urgent' },
  warning: { rank: 3, dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Action needed' },
  pending: { rank: 2, dot: 'bg-[#F26522]', chip: 'bg-orange-50 text-[#C24E12] border-orange-200', label: 'Due' },
  info: { rank: 1, dot: 'bg-blue-500', chip: 'bg-blue-50 text-blue-700 border-blue-200', label: 'FYI' },
};

const metricTone = (m) => {
  if (m.value == null || m.target == null) return 'text-slate-400';
  const ok = m.higher_is_better ? m.value >= m.target : m.value <= m.target;
  return ok ? 'text-emerald-600' : 'text-rose-600';
};

const METRIC_TESTID = { labor_percent: 'sales-snapshot-labor-percent', food_percent: 'sales-snapshot-food-percent', service_time_dt: 'sales-snapshot-service-time' };

const QUICK_BG = {
  1: 'bg-[#F26522] text-white hover:bg-[#d9551a]',
  2: 'bg-[#3E2418] text-white hover:bg-[#2a180f]',
  3: 'bg-slate-800 text-white hover:bg-slate-900',
  4: 'bg-white text-slate-900 border border-slate-200 hover:border-[#F26522] hover:text-[#F26522]',
  5: 'bg-orange-50 text-[#8a3c0d] border border-orange-200 hover:bg-orange-100',
};

export default function OpsHome() {
  const { user } = useAuth();
  const { stores, storeId, setStoreId } = useStores();
  const [data, setData] = useState(null);

  useEffect(() => { if (storeId) ops.get('/home', { store_id: storeId }).then(setData).catch(e => toast.error(e.response?.data?.detail || 'Failed to load')); }, [storeId]);

  if (!data) return <div className="p-6 text-slate-500" data-testid="home-loading">Loading restaurant…</div>;
  const d = data;
  const s = d.sales;

  // Build the prioritized reminders feed
  const reminders = [];
  d.overdue.forEach(x => reminders.push({ id: 'o' + x.id, sev: 'urgent', title: x.template_name, sub: `Was due ${x.due_date}`, to: `/audits-v2/${x.id}`, action: 'Complete now', testId: 'reminder-overdue' }));
  d.returned.forEach(x => reminders.push({ id: 'r' + x.id, sev: 'warning', title: `${x.template_name} returned`, sub: x.review?.comment || 'Needs changes', to: `/audits-v2/${x.id}`, action: 'Fix & resend', testId: 'reminder-returned' }));
  d.returned_reports.forEach(x => reminders.push({ id: 'rr' + x.id, sev: 'warning', title: `${x.report_type} report returned`, sub: x.comments?.at(-1)?.comment || 'Needs changes', to: `/reports-v2/${x.id}`, action: 'Fix report', testId: 'reminder-returned-report' }));
  (d.actions || []).forEach(a => reminders.push({ id: 'a' + a.id, sev: a.overdue ? 'urgent' : 'warning', title: a.title, sub: `${a.owner_name || 'Unassigned'} · due ${a.due_date}${a.overdue ? ' · OVERDUE' : ''}`, to: `/actions/${a.id}`, action: 'View action', testId: 'reminder-action' }));
  d.due_today.forEach(x => reminders.push({ id: 'd' + x.id, sev: 'pending', title: x.template_name, sub: `${x.kind} · due ${x.due_time || 'today'}`, to: `/audits-v2/${x.id}`, action: 'Start', testId: 'reminder-due-today' }));
  if (d.weekly_report_due) reminders.push({ id: 'wk', sev: 'pending', title: `Weekly report · week ending ${d.week_ending}`, sub: d.weekly_report ? 'Draft — not submitted yet' : 'Not started', to: d.weekly_report ? `/reports-v2/${d.weekly_report.id}` : `/reports-v2/new?store=${storeId}&period=${d.week_ending}&type=weekly`, action: 'Submit report', testId: 'reminder-weekly-report' });
  (d.feedback || []).forEach((f, i) => reminders.push({ id: 'f' + i, sev: 'info', title: f.comment, sub: `${f.by} · ${f.about}`, to: f.link, action: 'Read', testId: 'reminder-feedback' }));
  reminders.sort((a, b) => SEV[b.sev].rank - SEV[a.sev].rank);
  const topReminders = reminders.slice(0, 8);

  const quick = [
    { rank: 1, title: "Run today's checklist", sub: d.due_today[0] ? d.due_today[0].template_name : 'View audits & checklists', icon: ClipboardCheck, to: d.due_today[0] ? `/audits-v2/${d.due_today[0].id}` : '/audits', testId: 'quick-action-run-checklist' },
    { rank: 2, title: 'Weekly report', sub: d.weekly_report_due ? 'Needs submitting' : 'Submitted', icon: FileSpreadsheet, to: d.weekly_report ? `/reports-v2/${d.weekly_report.id}` : `/reports-v2/new?store=${storeId}&period=${d.week_ending}&type=weekly`, testId: 'quick-action-weekly-report' },
    { rank: 3, title: 'Log equipment issue', sub: 'Fryer, grill, POS, soft serve', icon: Wrench, to: '/service-tickets', testId: 'quick-action-log-ticket' },
    { rank: 4, title: 'Corrective actions', sub: `${d.actions.length} open`, icon: AlertTriangle, to: '/actions', testId: 'quick-action-view-actions' },
    { rank: 5, title: 'Training', sub: 'Videos & knowledge checks', icon: GraduationCap, to: '/training', testId: 'quick-action-open-training' },
  ];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="ops-home">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold">Restaurant Home · {d.today}</p>
          <h1 className="text-2xl sm:text-3xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{d.store.name}</h1>
          <p className="text-xs text-slate-500 mt-1">Hi {user?.name?.split(' ')[0]} · {d.store.restaurant_type ? d.store.restaurant_type.replace('_', ' ') : 'type not set'}{d.store.area_manager_name ? ` · AM ${d.store.area_manager_name}` : ''}</p>
        </div>
        {stores.length > 1 && <StoreSwitcher stores={stores} storeId={storeId} setStoreId={setStoreId} />}
      </div>

      {/* Sales hero */}
      <section className="rounded-2xl overflow-hidden border border-[#E5E1DA] bg-white" data-testid="sales-snapshot">
        <div className="bg-[#3E2418] px-5 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">Net sales {s ? (s.is_current_week ? 'this week' : `· week ending ${s.week_ending}`) : ''}</p>
            <p className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="sales-snapshot-net-sales">
              {s && s.net_sales != null ? fmt(s.net_sales, '$') : '—'}
            </p>
          </div>
          {s && s.wow_change != null && (
            <div className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold ${s.wow_change >= 0 ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`} data-testid="sales-snapshot-wow">
              {s.wow_change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {s.wow_change >= 0 ? '+' : ''}{s.wow_change}% <span className="text-white/50 font-medium hidden sm:inline">vs last wk</span>
            </div>
          )}
        </div>
        {s ? (
          <div className="grid grid-cols-3 divide-x divide-[#E5E1DA]">
            {s.metrics.map(m => (
              <div key={m.key} className="px-3 sm:px-5 py-3.5 text-center" data-testid={METRIC_TESTID[m.key] || `sales-snapshot-${m.key}`}>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{m.label}</p>
                <p className={`text-xl sm:text-2xl font-black ${metricTone(m)}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{m.value != null ? fmt(m.value, m.unit) : '—'}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{m.target != null ? `Target ${fmt(m.target, m.unit)}` : 'No target'}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-4 text-sm text-slate-500">No sales entered for this week yet — submit the weekly report or import KPIs to see sales here.</div>
        )}
      </section>

      {/* Top 5 quick actions */}
      <section data-testid="quick-actions">
        <h2 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2.5">Top 5 quick actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 stagger">
          {quick.map(q => {
            const Icon = q.icon;
            return (
              <Link key={q.rank} to={q.to} data-testid={q.testId}
                className={`group rounded-2xl p-3.5 flex flex-col gap-2 min-h-[104px] transition-all active:scale-[0.98] ${QUICK_BG[q.rank]}`}>
                <Icon className="w-5 h-5" />
                <div className="mt-auto">
                  <p className="text-sm font-bold leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{q.title}</p>
                  <p className="text-[11px] opacity-70 leading-snug mt-0.5 line-clamp-1">{q.sub}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Important reminders */}
      <section className="bg-white rounded-2xl border border-[#E5E1DA] p-4 sm:p-5" data-testid="reminders-feed">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-[#F26522]" />
          <h2 className="font-bold text-[#3E2418] text-base" style={{ fontFamily: 'Chivo, sans-serif' }}>Important reminders</h2>
          <span className="ml-auto text-xs font-semibold text-slate-500">{reminders.length}</span>
        </div>
        {topReminders.length === 0 ? (
          <div className="flex items-center gap-2 py-6 text-emerald-600" data-testid="reminders-empty">
            <CheckCircle2 className="w-5 h-5" /><span className="text-sm font-medium">All caught up — nothing needs your attention right now.</span>
          </div>
        ) : (
          <div className="space-y-1.5">
            {topReminders.map(r => {
              const sev = SEV[r.sev];
              return (
                <Link key={r.id} to={r.to} data-testid={r.testId}
                  className="flex items-center gap-3 rounded-xl border border-slate-100 hover:border-[#F26522]/40 hover:bg-orange-50/40 px-3 py-2.5 transition-colors min-h-[52px]">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${sev.dot}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">{r.title}</p>
                    <p className="text-xs text-slate-500 truncate">{r.sub}</p>
                  </div>
                  <span className={`shrink-0 hidden sm:inline-flex items-center gap-1 text-xs font-semibold rounded-full border px-2.5 py-1 ${sev.chip}`}>
                    {r.action} <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              );
            })}
            {reminders.length > topReminders.length && (
              <Link to="/actions" className="inline-flex items-center gap-1 text-xs font-semibold text-[#F26522] pt-1" data-testid="reminders-view-all">
                View all {reminders.length} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        )}
      </section>

      {/* Done today (reassurance) */}
      {d.done_today.length > 0 && (
        <section className="bg-white rounded-2xl border border-[#E5E1DA] p-4 sm:p-5" data-testid="done-today">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <h2 className="font-bold text-[#3E2418] text-base" style={{ fontFamily: 'Chivo, sans-serif' }}>Completed today</h2>
            <span className="ml-auto text-xs font-semibold text-slate-500">{d.done_today.length}</span>
          </div>
          <div className="space-y-1">
            {d.done_today.map(x => (
              <Link key={x.id} to={`/audits-v2/${x.id}`} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 last:border-0 hover:bg-emerald-50/40 -mx-2 px-2 rounded-lg">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{x.template_name}</p>
                  <p className="text-xs text-slate-400 truncate">By {x.submitted_by}{x.score != null ? ` · ${x.score}%` : ''}</p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
